import React from 'react';
import { ContentBlock, CompositeDecorator } from 'draft-js';
import TwitterText from 'twitter-text';
import { Mention, Hashtag, Url } from './Styled';

type StrategyCallback = (start: number, end: number) => void;

interface StrategyComponentProps {
  offsetKey: string;
  children?: React.ReactNode;
}

// Función genérica para encontrar patrones en el texto
const findPatternsInText = (
  text: string,
  pattern: RegExp,
  callback: StrategyCallback
) => {
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    callback(match.index, match.index + match[0].length);
  }
};

const extractMentionsLinkedin = (text: string): string[] => {
  const mentionRegex = /@\[([^\]]+)\]/g;
  const matches = text.match(mentionRegex) || [];
  return matches.map(mention => mention.match(/@\[(.*?)\]/)?.[1] || '');
};

const extractMentions = (text: string) =>
  TwitterText.extractMentions(text).map(mention => `@${mention}`);

const extractHashtags = (text: string) =>
  TwitterText.extractHashtags(text).map(hashtag => `#${hashtag}`);

const extractUrls = TwitterText.extractUrls;

const createMentionStrategy = (extractFn: (text: string) => string[]) => (
  contentBlock: ContentBlock,
  callback: StrategyCallback
) => {
  const text = contentBlock.getText();
  const mentions = extractFn(text);
  mentions.forEach(mention => {
    const escapedMention = mention.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    findPatternsInText(text, new RegExp(escapedMention, 'g'), callback);
  });
};

const createHashtagStrategy = (extractFn: (text: string) => string[]) => (
  contentBlock: ContentBlock,
  callback: StrategyCallback
) => {
  const text = contentBlock.getText();
  const hashtags = extractFn(text);
  hashtags.forEach(hashtag => {
    findPatternsInText(text, new RegExp(hashtag, 'g'), callback);
  });
};

const MentionSpan: React.FC<StrategyComponentProps> = ({
  children,
  offsetKey
}) => <Mention data-offset-key={offsetKey}>{children}</Mention>;

const HashtagSpan: React.FC<StrategyComponentProps> = ({
  children,
  offsetKey
}) => <Hashtag data-offset-key={offsetKey}>{children}</Hashtag>;

const UrlAnchor: React.FC<StrategyComponentProps> = ({
  children,
  offsetKey
}) => <Url data-offset-key={offsetKey}>{children}</Url>;

const TwitterDecorator = () =>
  new CompositeDecorator([
    {
      strategy: createMentionStrategy(extractMentions),
      component: MentionSpan
    },
    {
      strategy: createHashtagStrategy(extractHashtags),
      component: HashtagSpan
    },
    {
      strategy: (contentBlock, callback) => {
        const text = contentBlock.getText();
        const urls = extractUrls(text);
        urls.forEach(url => {
          findPatternsInText(
            text,
            new RegExp(url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            callback
          );
        });
      },
      component: UrlAnchor
    }
  ]);

const LinkedinDecorator = () =>
  new CompositeDecorator([
    {
      strategy: createMentionStrategy(extractMentionsLinkedin),
      component: MentionSpan
    },
    {
      strategy: createHashtagStrategy(extractHashtags),
      component: HashtagSpan
    },
    {
      strategy: (contentBlock, callback) => {
        const text = contentBlock.getText();
        const urls = extractUrls(text);
        urls.forEach(url => {
          findPatternsInText(
            text,
            new RegExp(url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            callback
          );
        });
      },
      component: UrlAnchor
    }
  ]);

export {
  TwitterDecorator,
  LinkedinDecorator,
  MentionSpan,
  HashtagSpan,
  UrlAnchor,
  extractMentions,
  extractHashtags,
  extractUrls,
  extractMentionsLinkedin
};
